import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Phone,
  CalendarCheck,
  Waves,
  Settings2,
  Smartphone,
  Bath,
  Sun,
  Wrench,
  LayoutGrid,
  Sparkles,
  Droplets,
  Check,
  Star,
  ArrowRight,
  MapPin,
  ShieldCheck,
  Award,
  HandHeart,
  HomeIcon,
  BadgeDollarSign,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import SiteHeader from "@/components/site/SiteHeader";
import SiteFooter from "@/components/site/SiteFooter";
import BeforeAfterSlider from "@/components/site/BeforeAfterSlider";
import MediaCarousel from "@/components/site/MediaCarousel";

const SERVICES = [
  {
    icon: Waves,
    title: "Complete Pool Remodels",
    desc: "Full pool transformations, modern designs and finishes, structural renovations.",
  },
  {
    icon: Settings2,
    title: "Equipment Upgrades",
    desc: "Energy-efficient pumps, filters, heaters, salt systems, and LED color-changing lights.",
  },
  {
    icon: Smartphone,
    title: "Pool Automation",
    desc: "Smart phone controls, automated lighting, pump and heater management.",
  },
  {
    icon: Bath,
    title: "Spa Additions",
    desc: "Custom integrated spas, spillover spas, luxury relaxation features.",
  },
  {
    icon: Sun,
    title: "Sun Shelf Additions",
    desc: "Sunshelves, tanning ledges, lounge areas for ultimate relaxation.",
  },
  {
    icon: Wrench,
    title: "New Plumbing Installation",
    desc: "Plumbing replacement, leak prevention, improved circulation.",
  },
  {
    icon: LayoutGrid,
    title: "Tile & Coping Renovations",
    desc: "Modern tile selections, travertine coping, premium materials.",
  },
  {
    icon: Sparkles,
    title: "Interior Finish Installation",
    desc: "Quartz, pebble finishes, and premium plaster systems.",
  },
  {
    icon: Droplets,
    title: "Water Features",
    desc: "Waterfalls, bubblers, deck jets, and laminar jets for a luxury, resort-style feel.",
  },
];

const WHY = [
  { icon: ShieldCheck, text: "Licensed & Experienced Professionals" },
  { icon: Award, text: "Quality Craftsmanship" },
  { icon: Sparkles, text: "Premium Materials" },
  { icon: BadgeDollarSign, text: "Honest, Transparent Pricing" },
  { icon: HomeIcon, text: "Local South Florida Company" },
  { icon: CalendarCheck, text: "Free On-Site Estimates" },
  { icon: HandHeart, text: "Customer Satisfaction Focused" },
];

const AREAS = [
  "Pompano Beach",
  "Fort Lauderdale",
  "Lighthouse Point",
  "Deerfield Beach",
  "Boca Raton",
  "Delray Beach",
  "Boynton Beach",
  "Coral Springs",
  "Parkland",
  "Coconut Creek",
  "Plantation",
  "Palm Beach County",
  "Broward County",
];

const TESTIMONIALS = [
  {
    name: "Jennifer & Mark Reyes",
    city: "Boca Raton, FL",
    project: "Complete Remodel + Spa Addition",
    image: "https://images.unsplash.com/photo-1758523671893-0ba21cf4260f?w=400",
    quote:
      "AZ Pools turned our outdated 90s pool into a resort-style retreat. The pebble finish and travertine coping are stunning — neighbors keep asking who did the work.",
  },
  {
    name: "Daniel Costa",
    city: "Fort Lauderdale, FL",
    project: "Equipment Upgrade + Automation",
    image: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=400",
    quote:
      "The new variable-speed pump and automation system cut our energy bill in half. Communication was honest from quote to final walkthrough. Highly recommend.",
  },
  {
    name: "Alicia Thompson",
    city: "Delray Beach, FL",
    project: "Sun Shelf + Tile Renovation",
    image: "https://images.pexels.com/photos/7579046/pexels-photo-7579046.jpeg?w=400",
    quote:
      "From the first estimate, the AZ Pools crew was respectful, on-time, and detail oriented. Our new Sunshelf is the favorite spot in the entire backyard.",
  },
];

const SERVICE_OPTIONS = [
  "Complete Pool Remodel",
  "Equipment Upgrade",
  "Pool Automation",
  "Spa Addition",
  "Sun Shelf Addition",
  "New Plumbing",
  "Tile & Coping",
  "Interior Finish",
  "Water Features",
  "Not Sure — Need Consultation",
];

const fadeUp = {
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.2 },
  transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
};

const DEFAULT_HERO_BG =
  "https://images.pexels.com/photos/8134745/pexels-photo-8134745.jpeg?w=2000";
const DEFAULT_ABOUT_1 =
  "https://images.pexels.com/photos/28271343/pexels-photo-28271343.jpeg?w=800";
const DEFAULT_ABOUT_2 =
  "https://images.unsplash.com/photo-1562016600-ece13e8ba570?w=800";
const DEFAULT_HERO_SUBHEADLINE =
  "Expert pool construction, renovations, equipment upgrades & custom enhancements throughout South Florida.";

export default function HomePage() {
  const [gallery, setGallery] = useState([]);
  const [activeProject, setActiveProject] = useState(0);
  const [siteImages, setSiteImages] = useState({});
  const [siteContent, setSiteContent] = useState({});
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    city: "",
    service: "",
    description: "",
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api
      .get("/gallery")
      .then((res) => setGallery(res.data || []))
      .catch(() => setGallery([]));
    api
      .get("/site-images")
      .then((res) => setSiteImages(res.data || {}))
      .catch(() => setSiteImages({}));
    api
      .get("/site-content")
      .then((res) => setSiteContent(res.data || {}))
      .catch(() => setSiteContent({}));
  }, []);

  const heroBg = siteImages.hero_background || DEFAULT_HERO_BG;
  const about1 = siteImages.about_image_1 || DEFAULT_ABOUT_1;
  const about2 = siteImages.about_image_2 || DEFAULT_ABOUT_2;
  const heroHeadline = siteContent.hero_headline; // when set, override default styled markup
  const heroSubheadline = siteContent.hero_subheadline || DEFAULT_HERO_SUBHEADLINE;

  const handleChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await api.post("/leads", form);
      toast.success("Thanks! We'll be in touch shortly.", {
        description: "Your free estimate request has been received.",
      });
      setForm({ name: "", phone: "", email: "", city: "", service: "", description: "" });
    } catch (err) {
      toast.error("Something went wrong", {
        description: err?.response?.data?.detail || "Please try again or call 954-328-8083.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const current = gallery[activeProject];

  return (
    <div className="bg-white text-slate-900">
      <SiteHeader />

      {/* HERO */}
      <section className="relative min-h-[100vh] flex items-end pb-20 md:pb-28 overflow-hidden">
        <div
          className="absolute inset-0 kenburns"
          style={{
            backgroundImage: `url('${heroBg}')`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0F3A5A]/60 via-[#0F3A5A]/40 to-[#0F3A5A]/90" />

        <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-16 w-full pt-32">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="text-xs md:text-sm uppercase tracking-[0.3em] font-semibold text-[#00B4D8] mb-5"
            data-testid="hero-overline"
          >
            Proudly Serving South Florida
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.2 }}
            className="font-display text-4xl sm:text-6xl lg:text-7xl xl:text-[88px] font-light text-white leading-[1.02] tracking-tighter max-w-5xl"
            data-testid="hero-headline"
          >
            {heroHeadline ? (
              heroHeadline
            ) : (
              <>
                Transform Your Backyard
                <br />
                Into a <span className="italic font-semibold text-[#A8E6F0]">Luxury Oasis</span>
              </>
            )}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-base md:text-xl text-slate-100/90 mt-7 max-w-2xl leading-relaxed"
            data-testid="hero-subheadline"
          >
            {heroSubheadline}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.55 }}
            className="mt-10 flex flex-col sm:flex-row gap-4"
          >
            <Link
              to="/quote"
              className="inline-flex items-center justify-center gap-2 bg-[#00B4D8] hover:bg-[#0096B4] text-white px-8 py-4 rounded-full font-semibold shadow-xl hover:shadow-2xl hover:-translate-y-0.5 transition-all"
              data-testid="hero-cta-estimate"
            >
              <CalendarCheck className="w-5 h-5" />
              Get Instant Quote
            </Link>
            <a
              href="tel:9543288083"
              className="inline-flex items-center justify-center gap-2 bg-white/10 backdrop-blur-md border border-white/30 hover:bg-white hover:text-[#0F3A5A] text-white px-8 py-4 rounded-full font-semibold transition-all"
              data-testid="hero-cta-call"
            >
              <Phone className="w-5 h-5" />
              Call Now: 954-328-8083
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.9 }}
            className="mt-16 grid grid-cols-3 gap-6 max-w-xl"
          >
            {[
              { v: "15+", l: "Years Experience" },
              { v: "500+", l: "Projects Completed" },
              { v: "5★", l: "Average Rating" },
            ].map((s) => (
              <div key={s.l} className="border-l border-white/30 pl-4">
                <p className="font-display text-3xl md:text-4xl font-light text-white">{s.v}</p>
                <p className="text-xs uppercase tracking-wider text-slate-200/80 mt-1">{s.l}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ABOUT */}
      <section id="about" className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 grid lg:grid-cols-2 gap-16 items-center">
          <motion.div {...fadeUp}>
            <p className="text-xs uppercase tracking-[0.3em] font-semibold text-[#00B4D8] mb-5">
              About AZ Pools
            </p>
            <h2 className="font-display text-4xl md:text-5xl font-light tracking-tight text-slate-900 leading-[1.05]">
              South Florida's trusted partner for
              <span className="italic font-medium text-[#0F3A5A]"> backyard transformations.</span>
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed mt-7">
              AZ Pools specializes in transforming outdated swimming pools into stunning
              backyard retreats. With years of experience serving South Florida homeowners,
              we deliver high-quality craftsmanship, reliable service, and premium materials
              on every project.
            </p>
            <p className="text-lg text-slate-600 leading-relaxed mt-4">
              Our mission is simple: exceptional pool renovations that increase the beauty,
              functionality, and value of your home.
            </p>
            <div className="mt-9 flex flex-wrap gap-3">
              {["Licensed", "Insured", "Family-Owned", "Free Estimates"].map((t) => (
                <span
                  key={t}
                  className="px-4 py-1.5 rounded-full bg-slate-100 text-slate-700 text-sm font-medium"
                >
                  {t}
                </span>
              ))}
            </div>
          </motion.div>

          <motion.div {...fadeUp} className="grid grid-cols-2 gap-4">
            <img
              src={about1}
              alt="Modern luxury pool"
              className="rounded-2xl aspect-[3/4] object-cover w-full"
            />
            <img
              src={about2}
              alt="Crystal blue pool water"
              className="rounded-2xl aspect-[3/4] object-cover w-full mt-10"
            />
          </motion.div>
        </div>
      </section>

      {/* SERVICES */}
      <section id="services" className="py-24 md:py-32 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <motion.div {...fadeUp} className="max-w-3xl mb-16">
            <p className="text-xs uppercase tracking-[0.3em] font-semibold text-[#00B4D8] mb-5">
              Our Services
            </p>
            <h2 className="font-display text-4xl md:text-5xl font-light tracking-tight text-slate-900 leading-[1.05]">
              Currently offering full-service
              <span className="italic font-medium text-[#0F3A5A]"> pool renovations.</span>
            </h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {SERVICES.map((s, i) => {
              const Icon = s.icon;
              return (
                <motion.div
                  key={s.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.6, delay: i * 0.05 }}
                  className="lift group bg-white border border-slate-200 rounded-2xl p-7 hover:border-[#00B4D8]/40"
                  data-testid={`service-card-${i}`}
                >
                  <div className="w-12 h-12 rounded-xl bg-[#0F3A5A] grid place-items-center mb-6 group-hover:bg-[#00B4D8] transition-colors">
                    <Icon className="w-5 h-5 text-white" strokeWidth={1.8} />
                  </div>
                  <h3 className="font-display text-lg font-semibold text-slate-900 mb-2.5 tracking-tight">
                    {s.title}
                  </h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{s.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* JANDY SERVICE PRO BANNER */}
      <section className="bg-slate-50 pb-24 md:pb-32" data-testid="jandy-banner">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="relative overflow-hidden rounded-3xl bg-white border border-[#00B4D8]/30 shadow-[0_20px_60px_-20px_rgba(15,58,90,0.25)] p-8 md:p-12"
          >
            {/* Decorative aqua gradient corner */}
            <div
              className="absolute -top-24 -right-24 w-72 h-72 rounded-full opacity-20 pointer-events-none"
              style={{ background: "radial-gradient(circle, #00B4D8 0%, transparent 70%)" }}
            />

            <div className="relative grid lg:grid-cols-[auto_1fr_auto] gap-10 items-center">
              {/* Badge */}
              <div className="flex items-center gap-4">
                <div className="relative w-20 h-20 rounded-full bg-[#0F3A5A] grid place-items-center shrink-0 shadow-lg">
                  <ShieldCheck className="w-10 h-10 text-[#00B4D8]" strokeWidth={1.8} />
                  <span className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-[#00B4D8] grid place-items-center text-white text-[10px] font-bold border-2 border-white">
                    PRO
                  </span>
                </div>
                <div className="lg:hidden">
                  <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#00B4D8]">
                    Authorized
                  </p>
                  <p className="font-display text-2xl font-semibold text-[#0F3A5A] tracking-tight">
                    Jandy® Service Pro
                  </p>
                </div>
              </div>

              {/* Content */}
              <div>
                <p className="hidden lg:block text-[11px] uppercase tracking-[0.3em] font-bold text-[#00B4D8] mb-2">
                  Authorized Installer
                </p>
                <h3 className="hidden lg:block font-display text-3xl md:text-4xl font-semibold text-[#0F3A5A] tracking-tight mb-3">
                  Jandy® Service Pro
                </h3>
                <p className="text-slate-600 text-base md:text-lg leading-relaxed max-w-2xl">
                  As a certified <strong className="text-[#0F3A5A]">Jandy® Service Pro installer</strong>,
                  we guarantee professional installation and an{" "}
                  <strong className="text-[#0F3A5A]">extended manufacturer warranty</strong> on every
                  piece of Jandy equipment we install — pumps, heaters, salt systems, automation, and
                  LED color-changing lights.
                </p>
              </div>

              {/* Trust pills */}
              <div className="grid grid-cols-2 lg:grid-cols-1 gap-3 lg:min-w-[220px]">
                <div className="flex items-center gap-2.5 bg-slate-50 rounded-xl px-4 py-3 border border-slate-200">
                  <Award className="w-4 h-4 text-[#00B4D8] shrink-0" />
                  <span className="text-xs font-semibold text-slate-700">Certified Installer</span>
                </div>
                <div className="flex items-center gap-2.5 bg-slate-50 rounded-xl px-4 py-3 border border-slate-200">
                  <ShieldCheck className="w-4 h-4 text-[#00B4D8] shrink-0" />
                  <span className="text-xs font-semibold text-slate-700">Extended Warranty</span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* WHY CHOOSE */}
      <section className="py-24 md:py-32 bg-[#0F3A5A] text-white relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.07] pointer-events-none"
          style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1562016600-ece13e8ba570?w=1600')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 relative">
          <motion.div {...fadeUp} className="max-w-3xl mb-14">
            <p className="text-xs uppercase tracking-[0.3em] font-semibold text-[#00B4D8] mb-5">
              Why Choose Us
            </p>
            <h2 className="font-display text-4xl md:text-5xl font-light tracking-tight text-white leading-[1.05]">
              Quality craftsmanship.
              <span className="italic font-medium text-[#A8E6F0]"> Honest pricing.</span>
            </h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-7">
            {WHY.map((w, i) => {
              const Icon = w.icon;
              return (
                <motion.div
                  key={w.text}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.6, delay: i * 0.07 }}
                  className="flex items-start gap-4 border-b border-white/10 pb-5"
                >
                  <div className="w-10 h-10 rounded-full bg-[#00B4D8]/20 grid place-items-center shrink-0">
                    <Check className="w-5 h-5 text-[#00B4D8]" strokeWidth={2.5} />
                  </div>
                  <div>
                    <p className="font-display font-medium text-lg leading-snug">{w.text}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* GALLERY */}
      <section id="gallery" className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <motion.div {...fadeUp} className="max-w-3xl mb-14">
            <p className="text-xs uppercase tracking-[0.3em] font-semibold text-[#00B4D8] mb-5">
              Project Gallery
            </p>
            <h2 className="font-display text-4xl md:text-5xl font-light tracking-tight text-slate-900 leading-[1.05]">
              Real projects.
              <span className="italic font-medium text-[#0F3A5A]"> Real transformations.</span>
            </h2>
            <p className="text-lg text-slate-600 mt-6">
              Browse our recent work across South Florida — swipe or use the arrows to explore each project.
            </p>
          </motion.div>

          {gallery.length > 0 ? (
            <div className="grid lg:grid-cols-[1fr_320px] gap-8 items-start">
              <motion.div {...fadeUp}>
                {current && (
                  current.display_format === "slider" ? (
                    <BeforeAfterSlider
                      key={current.id}
                      before={current.before_url}
                      after={current.after_url}
                    />
                  ) : (
                    <MediaCarousel
                      key={current.id}
                      images={
                        current.images && current.images.length
                          ? current.images
                          : [current.before_url, current.after_url].filter(Boolean)
                      }
                      labels={current.display_format === "swipe" ? ["Before", "After"] : []}
                      format={current.display_format || "single"}
                    />
                  )
                )}
                {current && (
                  <div className="mt-6">
                    <p className="text-xs uppercase tracking-[0.25em] text-[#00B4D8] font-semibold mb-2">
                      {current.category}
                    </p>
                    <h3 className="font-display text-2xl font-semibold text-slate-900">
                      {current.title}
                    </h3>
                    {current.description && (
                      <p className="text-slate-600 mt-2">{current.description}</p>
                    )}
                  </div>
                )}
              </motion.div>

              <div className="space-y-3" data-testid="gallery-thumbs">
                {gallery.map((g, i) => (
                  <button
                    key={g.id}
                    onClick={() => setActiveProject(i)}
                    className={`relative w-full flex items-center gap-4 p-3 rounded-2xl border text-left transition-all ${
                      i === activeProject
                        ? "border-[#0F3A5A] bg-slate-50 shadow-md"
                        : "border-slate-200 hover:border-slate-300"
                    }`}
                    data-testid={`gallery-thumb-${i}`}
                  >
                    {g.featured && (
                      <span className="absolute -top-1.5 -left-1.5 bg-[#00B4D8] text-white text-[9px] font-bold uppercase tracking-[0.18em] px-1.5 py-0.5 rounded-full shadow">
                        ★
                      </span>
                    )}
                    <img
                      src={g.after_url || (g.images && g.images[0]) || g.before_url}
                      alt={g.title}
                      className="w-20 h-20 rounded-xl object-cover shrink-0"
                    />
                    <div className="min-w-0">
                      <p className="text-[10px] uppercase tracking-[0.2em] text-[#00B4D8] font-semibold">
                        {g.category}
                      </p>
                      <p className="font-display font-medium text-slate-900 text-sm leading-tight mt-1 truncate">
                        {g.title}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <p className="text-slate-500">Gallery loading…</p>
          )}
        </div>
      </section>

      {/* AREAS */}
      <section id="areas" className="py-24 md:py-32 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <motion.div {...fadeUp} className="max-w-3xl mb-12">
            <p className="text-xs uppercase tracking-[0.3em] font-semibold text-[#00B4D8] mb-5">
              Service Areas
            </p>
            <h2 className="font-display text-4xl md:text-5xl font-light tracking-tight text-slate-900 leading-[1.05]">
              Proudly serving
              <span className="italic font-medium text-[#0F3A5A]"> South Florida.</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {AREAS.map((a, i) => (
              <motion.div
                key={a}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.5, delay: i * 0.03 }}
                className="bg-white border border-slate-200 rounded-2xl p-5 flex items-center gap-3 hover:border-[#00B4D8]/40 transition"
              >
                <MapPin className="w-4 h-4 text-[#00B4D8] shrink-0" />
                <span className="font-medium text-slate-800 text-sm">{a}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section id="testimonials" className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <motion.div {...fadeUp} className="max-w-3xl mb-14">
            <p className="text-xs uppercase tracking-[0.3em] font-semibold text-[#00B4D8] mb-5">
              Reviews
            </p>
            <h2 className="font-display text-4xl md:text-5xl font-light tracking-tight text-slate-900 leading-[1.05]">
              What homeowners
              <span className="italic font-medium text-[#0F3A5A]"> are saying.</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <motion.div
                key={t.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                className="bg-slate-50 border border-slate-200 rounded-3xl p-8 flex flex-col"
              >
                <div className="flex gap-0.5 mb-5">
                  {[...Array(5)].map((_, k) => (
                    <Star key={k} className="w-4 h-4 text-[#00B4D8] fill-[#00B4D8]" />
                  ))}
                </div>
                <p className="text-slate-700 leading-relaxed flex-1">"{t.quote}"</p>
                <div className="mt-7 pt-6 border-t border-slate-200 flex items-center gap-4">
                  <img
                    src={t.image}
                    alt={t.name}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                  <div>
                    <p className="font-display font-semibold text-slate-900">{t.name}</p>
                    <p className="text-xs text-slate-500">{t.city}</p>
                  </div>
                </div>
                <p className="mt-4 text-[10px] uppercase tracking-[0.2em] text-[#00B4D8] font-semibold">
                  Project: {t.project}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact" className="py-24 md:py-32 bg-[#0F3A5A] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: "url('https://images.unsplash.com/photo-1562016600-ece13e8ba570?w=2000')",
          backgroundSize: "cover", backgroundPosition: "center"
        }} />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-16 grid lg:grid-cols-2 gap-14 items-start">
          <motion.div {...fadeUp} className="text-white">
            <p className="text-xs uppercase tracking-[0.3em] font-semibold text-[#00B4D8] mb-5">
              Get In Touch
            </p>
            <h2 className="font-display text-4xl md:text-5xl font-light tracking-tight leading-[1.05]">
              Ready to transform
              <span className="italic font-medium text-[#A8E6F0]"> your pool?</span>
            </h2>
            <p className="text-lg text-slate-200/90 leading-relaxed mt-7 max-w-lg">
              Contact AZ Pools today for a free consultation and estimate. Let us help
              you create the backyard you've always wanted.
            </p>

            <div className="mt-10 space-y-5">
              <a href="tel:9543288083" className="flex items-center gap-4 group" data-testid="contact-phone-link">
                <div className="w-12 h-12 rounded-full bg-[#00B4D8] grid place-items-center shrink-0 group-hover:scale-110 transition">
                  <Phone className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-[#A8E6F0]">Call</p>
                  <p className="font-display text-2xl font-semibold text-white">954-328-8083</p>
                </div>
              </a>

              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-white/10 border border-white/20 grid place-items-center shrink-0">
                  <MapPin className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-[#A8E6F0]">Service Area</p>
                  <p className="text-slate-100">Proudly serving South Florida</p>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.form
            {...fadeUp}
            onSubmit={onSubmit}
            className="bg-white rounded-3xl p-8 md:p-10 shadow-2xl"
            data-testid="lead-form"
          >
            {/* Honeypot — invisible to humans, bots fill it and get silently dropped */}
            <div
              aria-hidden="true"
              style={{
                position: "absolute",
                left: "-9999px",
                width: "1px",
                height: "1px",
                overflow: "hidden",
              }}
            >
              <label htmlFor="lead-website-hp">Leave this field blank</label>
              <input
                id="lead-website-hp"
                type="text"
                name="website"
                tabIndex={-1}
                autoComplete="off"
                value={form.website || ""}
                onChange={(e) => setForm((f) => ({ ...f, website: e.target.value }))}
              />
            </div>
            <h3 className="font-display text-2xl font-semibold text-slate-900 mb-2">
              Schedule Your Free Estimate
            </h3>
            <p className="text-sm text-slate-500 mb-7">
              We'll respond within 24 hours.
            </p>

            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Name" name="name" value={form.name} onChange={handleChange} required testid="form-name" />
              <Field label="Phone" name="phone" value={form.phone} onChange={handleChange} required testid="form-phone" type="tel" />
              <Field label="Email" name="email" value={form.email} onChange={handleChange} required testid="form-email" type="email" className="sm:col-span-2" />
              <Field label="City" name="city" value={form.city} onChange={handleChange} required testid="form-city" />
              <div>
                <label className="block text-xs uppercase tracking-[0.2em] font-semibold text-slate-500 mb-2">
                  Service Needed
                </label>
                <select
                  name="service"
                  value={form.service}
                  onChange={handleChange}
                  required
                  data-testid="form-service"
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-900 focus:outline-none focus:border-[#00B4D8] focus:ring-2 focus:ring-[#00B4D8]/20 transition"
                >
                  <option value="">Select a service…</option>
                  {SERVICE_OPTIONS.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs uppercase tracking-[0.2em] font-semibold text-slate-500 mb-2">
                  Project Description
                </label>
                <textarea
                  name="description"
                  value={form.description}
                  onChange={handleChange}
                  required
                  rows={4}
                  data-testid="form-description"
                  placeholder="Tell us about your project, pool size, timeline…"
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-900 focus:outline-none focus:border-[#00B4D8] focus:ring-2 focus:ring-[#00B4D8]/20 transition resize-none"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={submitting}
              data-testid="form-submit"
              className="mt-7 w-full inline-flex items-center justify-center gap-2 bg-[#0F3A5A] hover:bg-[#174E7A] disabled:opacity-60 text-white px-8 py-4 rounded-full font-semibold shadow-lg hover:shadow-xl transition-all"
            >
              {submitting ? "Sending…" : (
                <>
                  Schedule My Free Estimate
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
            <p className="text-[11px] text-slate-400 text-center mt-4">
              By submitting, you agree to be contacted about your project. We never share your info.
            </p>
          </motion.form>
        </div>
      </section>

      <SiteFooter />

      {/* Sticky mobile call CTA */}
      <a
        href="tel:9543288083"
        data-testid="mobile-call-bar"
        className="lg:hidden fixed bottom-5 left-4 z-40 inline-flex items-center gap-2 whitespace-nowrap bg-[#00B4D8] hover:bg-[#0096B4] active:scale-95 text-white px-5 py-3 rounded-full font-semibold text-sm shadow-[0_18px_40px_-10px_rgba(0,180,216,0.7)] transition-all"
        aria-label="Call AZ Pools at 954-328-8083"
      >
        <span className="relative flex w-2 h-2">
          <span className="absolute inline-flex h-full w-full rounded-full bg-white opacity-75 animate-ping" />
          <span className="relative inline-flex rounded-full h-2 w-2 bg-white" />
        </span>
        <Phone className="w-4 h-4" strokeWidth={2.4} />
        Call 954-328-8083
      </a>
    </div>
  );
}

function Field({ label, name, value, onChange, required, type = "text", testid, className = "" }) {
  return (
    <div className={className}>
      <label className="block text-xs uppercase tracking-[0.2em] font-semibold text-slate-500 mb-2">
        {label}
      </label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        data-testid={testid}
        className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-[#00B4D8] focus:ring-2 focus:ring-[#00B4D8]/20 transition"
      />
    </div>
  );
}
